import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { JSONContent } from "@tiptap/react";

export interface ClimateActionCreatePrefill {
  title: string;
  descriptionText?: string;
  descriptionJson: JSONContent | null;
  focusAreaName?: string | null;
  sourceCatalogEntryId?: string | null;
}

interface ClimateActionCreateHandlerContextType {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  prefill: ClimateActionCreatePrefill | null;
  openCreateDialogWithPrefill: (prefill: ClimateActionCreatePrefill) => void;
}

const ClimateActionCreateHandlerContext = createContext<ClimateActionCreateHandlerContextType | undefined>(undefined);

interface ClimateActionCreateHandlerProviderProps {
  children: ReactNode;
}

export const ClimateActionCreateHandlerProvider: FC<ClimateActionCreateHandlerProviderProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [prefill, setPrefill] = useState<ClimateActionCreatePrefill | null>(null);

  useEffect(() => {
    if (!showModal) {
      setPrefill(null);
    }
  }, [showModal]);

  const openCreateDialogWithPrefill = useCallback((next: ClimateActionCreatePrefill) => {
    setPrefill(next);
    setShowModal(true);
  }, []);

  const value = useMemo(
    () => ({ showModal, setShowModal, prefill, openCreateDialogWithPrefill }),
    [showModal, setShowModal, prefill, openCreateDialogWithPrefill],
  );

  return (
    <ClimateActionCreateHandlerContext.Provider value={value}>
      {children}
    </ClimateActionCreateHandlerContext.Provider>
  );
};

export const useClimateActionCreateHandler = (): ClimateActionCreateHandlerContextType => {
  const context = useContext(ClimateActionCreateHandlerContext);

  if (!context) {
    throw new Error("useClimateActionCreateHandler must be used within a ChildProvider");
  }

  return context;
};
